
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Database } from '@/integrations/supabase/types';

type Reward = Database['public']['Tables']['rewards']['Row'];

const DEFAULT_FORM_VALUES = {
  name: '',
  description: '',
  points_required: 0,
  quantity_available: 0,
  tier_required: 'standard',
  image_url: '',
  valid_until: '',
  is_active: true
};

export const useRewardManagement = () => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRewards(data || []);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorFetchingRewards'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Handle checkboxes
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormValues(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setFormValues(DEFAULT_FORM_VALUES);
    setIsAddingNew(false);
    setEditingReward(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Prepare data
      const rewardData = {
        name: formValues.name,
        description: formValues.description,
        points_required: formValues.points_required,
        quantity_available: formValues.quantity_available || null,
        tier_required: formValues.tier_required,
        image_url: formValues.image_url || null,
        valid_until: formValues.valid_until ? new Date(formValues.valid_until).toISOString() : null,
        is_active: formValues.is_active
      };

      // Update or create
      if (editingReward) {
        const { error } = await supabase
          .from('rewards')
          .update(rewardData)
          .eq('id', editingReward.id);

        if (error) throw error;
        toast({
          title: t('admin.success'),
          description: t('admin.rewardUpdated'),
        });
      } else {
        const { error } = await supabase
          .from('rewards')
          .insert([rewardData]);

        if (error) throw error;
        toast({
          title: t('admin.success'),
          description: t('admin.rewardCreated'),
        });
      }

      // Reset form and refresh data
      resetForm();
      fetchRewards();
    } catch (error) {
      console.error('Error saving reward:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorSavingReward'),
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (reward: Reward) => {
    setEditingReward(reward);
    setFormValues({
      name: reward.name,
      description: reward.description || '',
      points_required: reward.points_required,
      quantity_available: reward.quantity_available || 0,
      tier_required: reward.tier_required || 'standard',
      image_url: reward.image_url || '',
      valid_until: reward.valid_until ? new Date(reward.valid_until).toISOString().split('T')[0] : '',
      is_active: reward.is_active
    });
    setIsAddingNew(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('admin.confirmDelete'))) return;
    
    try {
      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: t('admin.success'),
        description: t('admin.rewardDeleted'),
      });
      
      fetchRewards();
    } catch (error) {
      console.error('Error deleting reward:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorDeletingReward'),
        variant: 'destructive',
      });
    }
  };

  const handleToggleActive = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('rewards')
        .update({ is_active: !currentState })
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: t('admin.success'),
        description: currentState 
          ? t('admin.rewardDeactivated') 
          : t('admin.rewardActivated'),
      });
      
      fetchRewards();
    } catch (error) {
      console.error('Error toggling reward status:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorTogglingStatus'),
        variant: 'destructive',
      });
    }
  };

  return {
    rewards,
    loading,
    isAddingNew,
    setIsAddingNew,
    editingReward,
    formValues,
    handleInputChange,
    handleSubmit,
    resetForm,
    handleEdit,
    handleDelete,
    handleToggleActive
  };
};
